import { Link } from 'react-router-dom'
import { Calendar, User } from 'lucide-react'
import type { LabOrder } from '../../store/labOrdersSlice'

interface LabOrderDetailHeaderProps {
  order: LabOrder
}

const STATUS_STEPS = ['Ordered', 'Sample Collected', 'Sent to Lab', 'Results Received', 'Reviewed'] as const

function formatLabOrderId(num: number): string {
  return `LAB-${String(num).padStart(4, '0')}`
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'Ordered':
      return 'bg-[#e0e4e8] text-status-info'
    case 'Sample Collected':
      return 'bg-[#e8e0d0] text-[#8a6b30]'
    case 'Sent to Lab':
      return 'bg-accent-subtle-bg text-accent-dark'
    case 'Results Received':
      return 'bg-[#e8dff0] text-[#7a5a9a]'
    case 'Reviewed':
      return 'bg-accent-subtle-bg text-status-success'
    default:
      return 'bg-border text-text-muted'
  }
}

function priorityBadgeClass(priority: string): string {
  switch (priority) {
    case 'STAT':
      return 'bg-[#f0dede] text-status-error'
    case 'Urgent':
      return 'bg-[#f0e8d0] text-status-warning'
    default:
      return 'bg-border text-text-muted'
  }
}

export function LabOrderDetailHeader({ order }: LabOrderDetailHeaderProps) {
  const currentStepIndex = STATUS_STEPS.indexOf(order.status as typeof STATUS_STEPS[number])

  return (
    <div data-testid="lab-order-detail-header">
      {/* Title row */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h1 className="text-[16px] font-semibold text-text-primary" data-testid="lab-order-id">
          {formatLabOrderId(order.lab_order_number)}
        </h1>
        <span
          data-testid="lab-order-status-badge"
          className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${statusBadgeClass(order.status)}`}
        >
          {order.status}
        </span>
        <span
          data-testid="lab-order-priority-badge"
          className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${priorityBadgeClass(order.priority)}`}
        >
          {order.priority}
        </span>
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-4 text-[12px] text-text-muted mb-4 flex-wrap">
        <span className="inline-flex items-center gap-1" data-testid="lab-order-patient">
          <User size={13} />
          <Link
            to={`/patients/${order.patient_id}`}
            className="text-accent hover:underline"
            data-testid="lab-order-patient-link"
          >
            {order.patient_name}
          </Link>
        </span>
        {order.vet_name && (
          <span data-testid="lab-order-vet">
            Ordered by: {order.vet_name}
          </span>
        )}
        <span className="inline-flex items-center gap-1" data-testid="lab-order-date">
          <Calendar size={13} />
          {formatDate(order.date_ordered)}
        </span>
      </div>

      {/* Status progression indicator */}
      <div data-testid="status-progression" className="flex items-center gap-1 mb-2 overflow-x-auto max-sm:gap-0.5">
        {STATUS_STEPS.map((step, i) => {
          const isCompleted = i <= currentStepIndex
          return (
            <div key={step} className="flex items-center gap-1">
              {i > 0 && (
                <div
                  className={`w-6 h-0.5 ${i <= currentStepIndex ? 'bg-accent' : 'bg-border'}`}
                />
              )}
              <div className="flex flex-col items-center">
                <div
                  data-testid={`status-step-${step.toLowerCase().replace(/ /g, '-')}`}
                  className={`w-3 h-3 rounded-full border-2 ${
                    isCompleted
                      ? 'bg-accent border-accent'
                      : 'bg-surface border-border'
                  }`}
                />
                <span
                  className={`text-[10px] mt-1 whitespace-nowrap ${
                    isCompleted ? 'text-accent font-medium' : 'text-text-disabled'
                  }`}
                >
                  {step}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
